import { apiClient as api } from "./httpClient";
import type { PhoneCountry } from "../components/PhoneWithCountryInput";
import type { ContractStanding } from "./contractsService";

// Lista de pacientes (RN-306): leitura na base do tenant; a exclusão é lógica e
// tem escrita dupla no Bubble enquanto ele for o master de patients (RN-302).

export interface PatientListItem {
  id: string;
  patientName: string;
  patientEmail: string | null;
  patientBirthDate: string | null;
  patientGender: string | null;
  genderName: string | null;
  patientDocument: string | null;
  patientPhone: string | null;
  patientPhoneCode: string | null;
  /** DDI só com dígitos; nulo = Brasil (55) */
  patientPhoneDdi: string | null;
  patientPicture: string | null;
  /** Há foto no storage (RN-113a)? Em true, a lista busca a miniatura em GET /patients/{id}/photo?size=thumb */
  hasPhoto: boolean;
  responsibleName1: string | null;
  responsibleName2: string | null;
  responsibleEmail1: string | null;
  responsiblePhone1: string | null;
  responsiblePhoneCode1: string | null;
  responsiblePhoneDdi1: string | null;
  completeRegister: boolean;
  isActive: boolean;
  /** Paciente de teste (RN-552): selo na tela, fora de relatórios e recebimentos */
  isTest?: boolean;
  professionalNames: string[];
  /** situação do contrato (RN-558e); nulo = sessão sem a permissão da aba Contratos */
  contract?: ContractStanding | null;
}

export interface PagedResult<T> {
  items: T[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface PatientsQueryParams {
  pageNumber: number;
  pageSize: number;
  search?: string;
  status?: "active" | "inactive" | "all";
  /** Pacientes de teste (RN-552): "hide" (padrão) esconde, "only" lista só eles, "all" mistura */
  test?: "hide" | "only" | "all";
  genderId?: string;
  /** CSV de ids de UEPR — paciente entra se tiver qualquer um deles */
  professionals?: string;
  completeRegister?: boolean;
  /** situação do contrato (RN-558e): "vigente" ou "pendente"; vazio = todos */
  contract?: "vigente" | "pendente";
  sortBy?: "name" | "document" | "birthDate";
  sortOrder?: "asc" | "desc";
}

export interface OptionItem {
  id: string;
  name: string;
}

export interface DocumentTypeOption {
  id: string;
  name: string;
  /** Tipo exige órgão expedidor (RG); CPF não. */
  requiresExpedition: boolean;
  minLength: number | null;
  maxLength: number | null;
}

export interface PatientListOptions {
  professionals: OptionItem[];
  /** Escopo do perfil (RN-523b): 'own' = filtro de profissionais travado no próprio */
  dataScope?: "own" | "entity";
  ownProfessionalIds?: string[];
  genders: OptionItem[];
  documentTypes: DocumentTypeOption[];
  /** País é relação no Bubble/tenant (catálogo countries) — nunca texto livre */
  countries: OptionItem[];
}

export interface PatientDetail extends PatientListItem {
  patientDocumentType: string | null;
  patientCountry: string | null;
  patientNativeLanguage: string | null;
  addressStreet: string | null;
  addressNumber: string | null;
  addressComplement: string | null;
  addressDistrict: string | null;
  addressCity: string | null;
  addressState: string | null;
  addressZipCode: string | null;
  patientIsResponsible: boolean;
  responsibleDocument1: string | null;
  responsibleDocument2: string | null;
  responsiblePhone2: string | null;
  responsiblePhoneCode2: string | null;
  responsiblePhoneDdi2: string | null;
  responsibleEmail2: string | null;
  responsibleFinance: string | null;
  notes: string | null;
  patientNotes: string | null;
  registerOrigin: string | null;
  referredBy: string | null;
  /** Há foto no storage? Em true, buscar em GET /patients/{id}/photo (a API streama). */
  hasPhoto: boolean;
}

export const patientsService = {
  getAll: async (params: PatientsQueryParams): Promise<PagedResult<PatientListItem>> => {
    const response = await api.get("/patients", { params });
    return response.data;
  },

  // Marca/desmarca paciente de teste (RN-552). O backend só aceita perfil Administrador.
  setTestFlag: async (id: string, isTest: boolean): Promise<void> => {
    await api.put(`/patients/${id}/test-flag`, { isTest });
  },

  getById: async (id: string): Promise<PatientDetail> => {
    const response = await api.get(`/patients/${id}`);
    return response.data;
  },

  getPresenceSummary: async (id: string): Promise<PresenceSummary> => {
    const response = await api.get(`/patients/${id}/presence-summary`);
    return response.data;
  },

  // Foto do paciente (RN-113a). O MinIO é interno: a API streama os bytes sob auth/escopo.
  // O <img> não manda header de auth → buscamos como blob (o interceptor injeta o JWT) e
  // devolvemos um object URL. Quem chama deve revogar (URL.revokeObjectURL) ao trocar/desmontar.
  getPhoto: async (id: string, thumb = false): Promise<string> => {
    const response = await api.get(`/patients/${id}/photo${thumb ? "?size=thumb" : ""}`, { responseType: "blob" });
    return URL.createObjectURL(response.data);
  },

  // Upload (multipart). O apiClient tem Content-Type: application/json como default e ele
  // VAZA para o FormData (a API respondia 415). Anulando o header aqui, o browser define
  // "multipart/form-data; boundary=..." sozinho. Devolve a URL relativa (cache-bust) da foto.
  setPhoto: async (id: string, blob: Blob): Promise<string> => {
    const form = new FormData();
    form.append("file", blob, "photo.jpg");
    const response = await api.post(`/patients/${id}/photo`, form, {
      headers: { "Content-Type": null },
    });
    return response.data.photoUrl as string;
  },

  removePhoto: async (id: string): Promise<void> => {
    await api.delete(`/patients/${id}/photo`);
  },

  getListOptions: async (): Promise<PatientListOptions> => {
    const response = await api.get("/patients/list-options");
    return response.data;
  },

  // Motivo vai no CORPO (não em query string): pode conter dado sensível do paciente
  // e query string fica registrada nos logs de acesso/proxy.
  remove: async (id: string, reason: string): Promise<void> => {
    await api.delete(`/patients/${id}`, { data: { reason } });
  },

  checkDocument: async (document: string): Promise<CheckDocumentResult> => {
    const response = await api.get("/patients/check-document", { params: { document } });
    return response.data;
  },

  quickRegister: async (payload: QuickRegisterPayload): Promise<QuickRegisterResult> => {
    const response = await api.post("/patients/quick-register", payload);
    return response.data;
  },

  // Cadastro completo (RN-112l): nasce no Bubble (RN-302) e devolve o id local
  /** Edição do cadastro completo (RN-112d): mesma carga do register; a API grava no Bubble e no tenant. */
  update: async (id: string, payload: RegisterPatientPayload): Promise<void> => {
    await api.put(`/patients/${id}/register`, payload);
  },

  register: async (payload: RegisterPatientPayload): Promise<string> => {
    const response = await api.post("/patients/register", payload);
    return response.data;
  },

  // Formulário público (RN-112f) — endpoints anônimos por alias + token
  getIntake: async (alias: string, token: string): Promise<PatientIntakeState> => {
    const response = await api.get(`/patient-intake/${encodeURIComponent(alias)}/${encodeURIComponent(token)}`);
    return response.data;
  },

  // Trava: confirma a data de nascimento e libera o pré-preenchimento
  verifyIntake: async (alias: string, token: string, birthDate: string): Promise<PatientIntakeState> => {
    const response = await api.post(
      `/patient-intake/${encodeURIComponent(alias)}/${encodeURIComponent(token)}/verify`,
      { birthDate });
    return response.data;
  },

  submitIntake: async (alias: string, token: string, form: PatientIntakeFormData): Promise<void> => {
    await api.post(`/patient-intake/${encodeURIComponent(alias)}/${encodeURIComponent(token)}`, form);
  },
};

export interface CheckDocumentResult {
  valid: boolean;
  exists: boolean;
  existingPatientName: string | null;
  existingPatientId: string | null;
}

export interface QuickRegisterResult {
  id: string;
  emailSent: boolean;
  intakeLink: string | null;
}

export interface PatientIntakeFormData {
  patientName: string | null;
  birthDate: string | null;
  genderId: string | null;
  document: string | null;
  email: string | null;
  phone: string | null;
  responsibleName1: string | null;
  responsibleDocument1: string | null;
  responsiblePhone1: string | null;
  responsibleEmail1: string | null;
  responsibleName2: string | null;
  responsibleDocument2: string | null;
  responsiblePhone2: string | null;
  responsibleEmail2: string | null;
  addressZipCode: string | null;
  addressStreet: string | null;
  addressNumber: string | null;
  addressComplement: string | null;
  addressDistrict: string | null;
  addressCity: string | null;
  addressState: string | null;
  /** observações escritas pelo paciente/responsável (nunca as internas) */
  patientNotes: string | null;
  registerOrigin: string | null;
  referredBy: string | null;
  /** trava anti-bypass: o submit revalida a data verificada */
  verificationBirthDate?: string | null;
  /** DDI de cada telefone (só dígitos); os telefones trazem o número nacional */
  phoneDdi?: string | null;
  responsiblePhoneDdi1?: string | null;
  responsiblePhoneDdi2?: string | null;
}

export interface PatientIntakeState {
  status: 'valid' | 'expired' | 'completed' | 'revoked' | 'invalid';
  clinicName: string | null;
  prefill: PatientIntakeFormData | null;
  genders: OptionItem[];
  /** países com DDI para os telefones — o formulário é anônimo e não lê /countries */
  phoneCountries: PhoneCountry[];
}

export interface PresenceCounts {
  attended: number;
  missed: number;
  cancelled: number;
}

export interface PresenceSummary {
  lastPresence: string | null;
  total: PresenceCounts;
  last30Days: PresenceCounts;
}

export interface RegisterPatientPayload {
  patientName: string;
  birthDate: string | null;
  genderId: string | null;
  documentTypeId: string | null;
  document: string | null;
  documentExpedition: string | null;
  phone: string | null;
  email: string | null;
  countryId: string | null;
  nativeLanguage: string | null;
  patientIsResponsible: boolean;
  responsibleName1: string | null;
  responsibleDocument1: string | null;
  responsiblePhone1: string | null;
  responsibleEmail1: string | null;
  responsibleName2: string | null;
  responsibleDocument2: string | null;
  responsiblePhone2: string | null;
  responsibleEmail2: string | null;
  /** 1 ou 2 — qual responsável é o financeiro (null = nenhum) */
  financialResponsible: number | null;
  addressZipCode: string | null;
  addressStreet: string | null;
  addressNumber: string | null;
  addressComplement: string | null;
  addressDistrict: string | null;
  addressCity: string | null;
  addressState: string | null;
  notes: string | null;
  registerOrigin: string | null;
  referredBy: string | null;
  completeRegister: boolean;
  phoneDdi: string | null;
  responsiblePhoneDdi1: string | null;
  responsiblePhoneDdi2: string | null;
}

export interface QuickRegisterPayload {
  patientName: string;
  birthDate?: string | null;
  document?: string | null;
  /** telefone/WhatsApp (número nacional) — vai para o responsável 1 ou para o paciente conforme a flag */
  phone?: string | null;
  phoneDdi?: string | null;
  email?: string | null;
  responsibleName?: string | null;
  contactsAreResponsible: boolean;
  notes?: string | null;
  /** RN-112f: gera o convite e envia o link de preenchimento ao email de contato */
  sendRegistrationEmail?: boolean;
  clinicAlias?: string | null;
}
